import { useState, useCallback } from 'react';

export const PRESET_DISCIPLINES = [
  {
    id: 'fasting',
    title: 'Fasting',
    description: 'Skip a meal, social media, or something you seek. Replace this time with seeking the Lord.',
    verse: 'Matthew 6:16-18',
    frequency: 'daily',
  },
  {
    id: 'prayer_morning',
    title: 'Morning Prayer',
    description: 'Start every day with a prayer of thanksgiving and submission to His Will.',
    verse: 'Mark 1:35',
    frequency: 'daily',
  },
  {
    id: 'prayer_evening',
    title: 'Evening Prayer',
    description: 'End every day with a prayer of thanksgiving and needs of others.',
    verse: '1 Thessalonians 5:17',
    frequency: 'daily',
  },
  {
    id: 'scripture',
    title: 'Read Scripture',
    description: 'At least 10 minutes of reading scripture.',
    verse: 'Joshua 1:8',
    frequency: 'daily',
  },
  {
    id: 'generosity',
    title: 'Generosity',
    description: 'At least one act that costs you — time, effort, money, or comfort.',
    verse: '2 Corinthians 9:7',
    frequency: 'daily',
  },
  {
    id: 'water',
    title: 'Water',
    description: 'Drink at least one gallon of water per day.',
    verse: '1 Corinthians 6:19',
    frequency: 'daily',
  },
  {
    id: 'activity',
    title: 'Activity',
    description: 'Exercise or walk for 30 minutes every day.',
    verse: '1 Timothy 4:8',
    frequency: 'daily',
  },
  {
    id: 'journaling',
    title: 'Journaling',
    description: 'Write down what God is revealing, where you were challenged, and what you are grateful for.',
    verse: 'Habakkuk 2:2',
    frequency: 'daily',
  },
  {
    id: 'forgiveness',
    title: 'Forgiveness',
    description: 'Release someone from a debt they owe you — spoken or unspoken, large or small.',
    verse: 'Matthew 18:21-22',
    frequency: 'daily',
  },
  {
    id: 'repentance',
    title: 'Repentance',
    description: 'Examine your heart. Confess where you fell short and turn back toward the Lord.',
    verse: 'Acts 3:19',
    frequency: 'daily',
  },
  {
    id: 'reflection',
    title: 'Reflection',
    description: 'Sit in silence. Meditate on the Word and listen for the still, small voice.',
    verse: 'Psalm 46:10',
    frequency: 'daily',
  },
];

const TOTAL_DAYS = 77;
const STORAGE_KEY = 'narrow77_data_v2';

export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function getDateKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function getDaysBetween(start, current) {
  const s = new Date(start + 'T00:00:00');
  const c = new Date(current + 'T00:00:00');
  return Math.floor((c - s) / (1000 * 60 * 60 * 24));
}

// Get the day-of-week index (0=Sun, 6=Sat) for a date key
export function getDayOfWeek(dateKey) {
  return new Date(dateKey + 'T00:00:00').getDay();
}

// Does this discipline apply on this date?
export function appliesToDate(disc, dateKey) {
  if (disc.frequency === 'daily') return true;
  if (disc.frequency === 'weekly') {
    return getDayOfWeek(dateKey) === disc.weeklyDay;
  }
  return true;
}

function loadData() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch (e) {
    console.error('Failed to load Narrow77 data', e);
  }
  return null; // null means setup needed
}

function saveData(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.error('Failed to save Narrow77 data', e);
  }
}

const useNarrow77 = () => {
  const [data, setData] = useState(loadData);

  const initChallenge = useCallback((selectedDisciplines) => {
    const today = getDateKey(new Date());
    const newData = {
      startDate: today,
      days: {},
      restarts: 0,
      disciplines: selectedDisciplines,
    };
    saveData(newData);
    setData(newData);
  }, []);

  const toggleDiscipline = useCallback((dateKey, disciplineId) => {
    setData(prev => {
      const dayData = prev.days[dateKey] || { checked: {}, journal: '' };
      const newChecked = { ...dayData.checked, [disciplineId]: !dayData.checked[disciplineId] };
      const newData = {
        ...prev,
        days: {
          ...prev.days,
          [dateKey]: { ...dayData, checked: newChecked },
        },
      };
      saveData(newData);
      return newData;
    });
  }, []);

  const saveJournal = useCallback((dateKey, text) => {
    setData(prev => {
      const dayData = prev.days[dateKey] || { checked: {} };
      const newData = {
        ...prev,
        days: {
          ...prev.days,
          [dateKey]: { ...dayData, journal: text },
        },
      };
      saveData(newData);
      return newData;
    });
  }, []);

  const restartChallenge = useCallback(() => {
    setData(prev => {
      const today = getDateKey(new Date());
      const newData = {
        ...prev,
        startDate: today,
        days: {},
        restarts: (prev.restarts || 0) + 1,
      };
      saveData(newData);
      return newData;
    });
  }, []);

  const fullReset = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setData(null);
  }, []);

  const updateDisciplines = useCallback((newDisciplines) => {
    setData(prev => {
      const newData = {
        ...prev,
        disciplines: newDisciplines,
      };
      saveData(newData);
      return newData;
    });
  }, []);

  // If data is null, setup is needed
  if (!data) {
    return {
      needsSetup: true,
      initChallenge,
      fullReset,
    };
  }

  const today = getDateKey(new Date());
  const currentDay = Math.min(Math.max(getDaysBetween(data.startDate, today) + 1, 1), TOTAL_DAYS);
  const disciplines = data.disciplines || [];

  // Get disciplines that apply on a specific date
  const getDisciplinesForDate = (dateKey) => {
    return disciplines.filter(disc => appliesToDate(disc, dateKey));
  };

  // Check if a day is fully complete — only counting disciplines that apply that day
  const isDayComplete = (dateKey) => {
    const applicable = getDisciplinesForDate(dateKey);
    if (applicable.length === 0) return true;
    return applicable.every(disc => data.days[dateKey]?.checked?.[disc.id]);
  };

  // Is a specific discipline fulfilled for a date?
  const isDisciplineFulfilled = (dateKey, disc) => {
    return data.days[dateKey]?.checked?.[disc.id];
  };

  // Calculate streak
  let streak = 0;
  for (let i = 0; i < currentDay; i++) {
    const d = new Date(data.startDate + 'T00:00:00');
    d.setDate(d.getDate() + i);
    const key = getDateKey(d);
    if (isDayComplete(key)) {
      streak++;
    } else if (key < today) {
      streak = 0;
    }
  }

  return {
    needsSetup: false,
    data,
    today,
    currentDay,
    totalDays: TOTAL_DAYS,
    streak,
    disciplines,
    toggleDiscipline,
    saveJournal,
    restartChallenge,
    fullReset,
    initChallenge,
    updateDisciplines,
    isDayComplete,
    isDisciplineFulfilled,
    getDisciplinesForDate,
  };
};

export default useNarrow77;
